import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Check, Star, Sparkles, ShieldCheck, ArrowRight, Heart, FileText, Bell } from "lucide-react";

const RECRUITER_PLANS = [
  {
    key: "free", name: "Free", tagline: "Get started",
    monthly: 0, annual: 0,
    cta: "Start free",
    features: [
      "2 active job postings",
      "100 applicants / job / month",
      "Standard listing visibility",
      "9-stage applicant pipeline",
      "Custom application forms",
      "Community support",
      "Terms can change with notice",
    ],
  },
  {
    key: "starter", name: "Starter", tagline: "For growing teams",
    monthly: 12.99, annual: 9.99,
    cta: "Choose Starter",
    features: [
      "10 active job postings",
      "Unlimited applicants per role",
      "1 Featured boost per month",
      "Custom application forms",
      "Up to 10 team members",
      "Email support",
      "Everything in Free",
    ],
  },
  {
    key: "growth", name: "Growth", tagline: "Scale your hiring",
    monthly: 29.99, annual: 24.99, highlight: true,
    cta: "Choose Growth",
    features: [
      "Unlimited active job postings",
      "Unlimited applicants",
      "3 Featured + 1 Premium Spotlight / month",
      "Up to 20 team members",
      "Priority candidate review",
      "Priority support",
      "Everything in Starter",
    ],
  },
  {
    key: "enterprise", name: "Enterprise", tagline: "For high-volume teams",
    monthly: null, annual: null, dark: true,
    cta: "Contact sales",
    features: [
      "Everything unlimited",
      "Dedicated success manager",
      "API access",
      "Custom branding",
      "SSO + advanced security",
      "Custom SLA",
    ],
  },
];

const CANDIDATE_PERKS = [
  { icon: Heart, t: "Unlimited applications", d: "Apply to as many roles as you want." },
  { icon: ShieldCheck, t: "Application status tracking", d: "See exactly where you stand across all 9 stages." },
  { icon: Bell, t: "Job alerts", d: "Get pinged when roles matching your interests are posted." },
  { icon: FileText, t: "Profile & resume storage", d: "Store up to 2 resumes plus saved info." },
  { icon: Sparkles, t: "Specialised hiring profile", d: "Disclose accommodations or specialised hiring needs (e.g. disabilities) once — applied automatically." },
  { icon: Check, t: "Direct messaging with recruiters", d: "When invited — no spam." },
];

export default function Pricing() {
  const [cycle, setCycle] = useState("monthly");
  const [dbPlans, setDbPlans] = useState(null);
  const [dbBoosts, setDbBoosts] = useState(null);
  const [dbCandidatePlans, setDbCandidatePlans] = useState([]);
  const [content, setContent] = useState({ headline: "", sub_headline: "", enterprise_blurb: "", faq: [] });

  useEffect(() => {
    api.get("/plans/catalog?category=subscription").then((r) => setDbPlans(r.data.plans || [])).catch(() => setDbPlans([]));
    api.get("/plans/catalog?category=boost").then((r) => setDbBoosts(r.data.plans || [])).catch(() => setDbBoosts([]));
    api.get("/plans/catalog?category=candidate").then((r) => setDbCandidatePlans(r.data.plans || [])).catch(() => setDbCandidatePlans([]));
    api.get("/pricing/content").then((r) => setContent({ ...r.data, faq: r.data.faq || [] })).catch(() => {});
  }, []);

  // Prefer Owner-managed plans; fall back to hardcoded defaults if none configured yet.
  const recruiterPlans = (dbPlans && dbPlans.length > 0)
    ? dbPlans.map((p) => {
        // Price display rules:
        //  - Enterprise / category=enterprise → "Custom" on both cycles
        //  - "Free" → £0
        //  - No price configured on a non-free plan → "Custom"
        const isEnterprise = p.category === "enterprise" || (p.name || "").toLowerCase() === "enterprise";
        const isFreeNamed = (p.name || "").toLowerCase() === "free";
        const hasMonthly = p.monthly_price_pence != null && p.monthly_price_pence > 0;
        const hasYearly = p.yearly_price_pence != null && p.yearly_price_pence > 0;
        let monthly, annual;
        if (isEnterprise) { monthly = null; annual = null; }
        else if (isFreeNamed) { monthly = 0; annual = 0; }
        else {
          monthly = hasMonthly ? p.monthly_price_pence / 100
                   : (p.billing_cycle === "yearly" ? null : (p.price_pence > 0 ? p.price_pence / 100 : null));
          annual = hasYearly ? Math.round(p.yearly_price_pence / 12) / 100
                  : (p.billing_cycle === "yearly" && p.price_pence > 0 ? Math.round(p.price_pence / 12) / 100 : null);
        }
        return {
          key: p.plan_id, name: p.name, tagline: p.description,
          monthly, annual,
          highlight: p.is_most_popular, dark: false,
          cta: p.cta_label || (isEnterprise ? "Contact sales" : "Choose plan"),
          cta_url: p.cta_url || "/register",
          tag: p.tag,
          features: p.features || [],
        };
      })
    : RECRUITER_PLANS;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="border-b border-[#E2E0D8]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-12">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 lg:col-span-8">
              <div className="label-eyebrow">Pricing</div>
              <h1 className="font-display font-black text-5xl lg:text-7xl mt-3 tracking-tight leading-[0.95]" data-testid="pricing-headline">
                {content.headline || (<>Made for small businesses. <br /><span className="text-[var(--brand-primary)]">Affordable</span> for small businesses.</>)}
              </h1>
              <p className="mt-6 text-lg text-[#3a3a3a] max-w-2xl leading-relaxed" data-testid="pricing-sub">
                {content.sub_headline || "Free plan covers most teams under 10. Fair Tiers, No Excessive 5 Year Contracts. We grow with you, not against you."}
              </p>
            </div>
            <div className="hidden lg:flex col-span-4 justify-end">
              <div className="border border-[#0F0F0F] px-5 py-4 text-right">
                <div className="label-eyebrow">Currency</div>
                <div className="font-display font-black text-3xl mt-1">GBP</div>
                <div className="text-xs text-[#5C5C5C]">Billed once per cycle</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CANDIDATE Plans */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="label-eyebrow">For Candidates</div>
        <h2 className="font-display font-black text-4xl lg:text-5xl mt-3 tracking-tight">
          Standard is free. <span className="italic font-medium">Or upgrade for premium perks.</span>
        </h2>

        <div className="mt-10 grid grid-cols-12 gap-6">
          <div data-testid="candidate-plan" className="col-span-12 lg:col-span-4 border-2 border-[#0F0F0F] p-8 flex flex-col bg-[#0F0F0F] text-[#FDFCF8] relative text-left">
            <span className="absolute -top-3.5 left-6 bg-[var(--brand-primary)] text-white text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5">
              Standard Account
            </span>
            <Heart className="w-7 h-7 text-[var(--brand-primary)]" />
            <h3 className="font-display font-black text-3xl mt-6 tracking-tight">Candidate Standard</h3>
            <p className="text-sm mt-1 text-[#a8a8a8]">Everything you need to land your next role</p>
            <div className="mt-8 flex items-baseline gap-1.5">
              <span className="font-display font-black text-6xl">£0</span>
              <span className="text-sm text-[#a8a8a8]">/ permanent</span>
            </div>
            <div className="mt-6 flex-1 border-t border-[#3a3a3a] pt-4 space-y-2 text-sm text-[#a8a8a8]">
              {CANDIDATE_PERKS.slice(0, 4).map((p, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <Check className="w-4 h-4 text-[var(--brand-primary)] shrink-0 mt-0.5" />
                  <span>{p.t}</span>
                </div>
              ))}
            </div>
            <Link to="/register" data-testid="candidate-cta" className="mt-8 bg-[var(--brand-primary)] text-white py-3 px-4 font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#B83A28] transition">
              Create free account <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Dynamic Candidate plans from database */}
          {dbCandidatePlans && dbCandidatePlans.map((p) => (
            <div key={p.plan_id} data-testid={`candidate-db-plan-${p.plan_id}`} className="col-span-12 lg:col-span-4 border-2 border-[#0F0F0F] p-8 flex flex-col bg-[#FDFCF8] text-[#0F0F0F] relative text-left">
              {p.tag && (
                <span className="absolute -top-3.5 left-6 bg-blue-600 text-white text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5">
                  {p.tag}
                </span>
              )}
              <Sparkles className="w-7 h-7 text-blue-600" />
              <h3 className="font-display font-black text-3xl mt-6 tracking-tight">{p.name}</h3>
              <p className="text-sm mt-1 text-[#5C5C5C]">{p.description}</p>
              <div className="mt-8 flex items-baseline gap-1.5">
                <span className="font-display font-black text-6xl">£{(p.price_pence / 100).toFixed(2)}</span>
                <span className="text-sm text-[#5C5C5C]">/ {p.billing_cycle || "month"}</span>
              </div>
              <div className="mt-6 flex-1 border-t border-[#E2E0D8] pt-4 space-y-2 text-sm text-[#3a3a3a]">
                {(p.features || []).map((f, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <Check className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <Link to={p.cta_url || "/register"} data-testid={`candidate-db-cta-${p.plan_id}`} className="mt-8 bg-[#0F0F0F] text-white py-3 px-4 font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#5C5C5C] transition">
                {p.cta_label || "Get Premium"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}

          {(!dbCandidatePlans || dbCandidatePlans.length === 0) && (
            <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
              {CANDIDATE_PERKS.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.t} className="bg-[#FDFCF8] p-5">
                    <Icon className="w-5 h-5 text-[var(--brand-primary)]" />
                    <div className="font-display font-bold mt-3 text-base">{p.t}</div>
                    <div className="text-sm text-[#3a3a3a] mt-1 leading-relaxed">{p.d}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* RECRUITER plans */}
      <section className="bg-[#F3F2EB] border-y border-[#E2E0D8]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="label-eyebrow">For Recruiters</div>
              <h2 className="font-display font-black text-4xl lg:text-5xl mt-3 tracking-tight">
                A range of plans. <span className="text-[var(--brand-primary)]">Cancel anytime.</span>
              </h2>
            </div>
            <div className="inline-flex border border-[#0F0F0F] rounded-none overflow-hidden">
              {["monthly", "annual"].map((c) => (
                <button key={c}
                  data-testid={`cycle-${c}`}
                  onClick={() => setCycle(c)}
                  className={`px-5 py-2 text-sm font-semibold ${cycle === c ? "bg-[#0F0F0F] text-[#FDFCF8]" : "bg-transparent hover:bg-[#0F0F0F]/5"}`}>
                  {c === "monthly" ? "Monthly" : "Annual (save up to 23%)"}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="recruiter-plans-grid">
            {recruiterPlans.map((p) => {
              const price = cycle === "annual" ? p.annual : p.monthly;
              return (
                <div key={p.key} data-testid={`plan-${p.key}`}
                  className={`relative border-2 ${p.highlight ? "border-[var(--brand-primary)] lg:-translate-y-3 bg-[#FDFCF8]" : p.dark ? "border-[#0F0F0F] bg-[#0F0F0F] text-[#FDFCF8]" : "border-[#E2E0D8] bg-[#FDFCF8]"} p-7 flex flex-col`}>
                  {p.tag && (
                    <span className="absolute -top-3.5 left-5 bg-[#0F0F0F] text-[#FDFCF8] text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5">
                      {p.tag}
                    </span>
                  )}
                  {p.highlight && !p.tag && (
                    <span className="absolute -top-3.5 left-5 bg-[var(--brand-primary)] text-white text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5">
                      Most popular
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold ${p.dark ? "text-[#a8a8a8]" : "text-[#5C5C5C]"}`}>
                      {p.name}
                    </span>
                  </div>
                  <h3 className="font-display font-black text-2xl mt-3 tracking-tight">{p.name}</h3>
                  <p className={`text-sm mt-1 ${p.dark ? "text-[#a8a8a8]" : "text-[#5C5C5C]"}`}>{p.tagline}</p>

                  <div className="mt-6 flex items-baseline gap-1">
                    {price == null ? (
                      <span className="font-display font-black text-4xl">Custom</span>
                    ) : price === 0 ? (
                      <>
                        <span className="font-display font-black text-5xl">£0</span>
                        <span className={`text-xs ${p.dark ? "text-[#a8a8a8]" : "text-[#5C5C5C]"}`}>/ month</span>
                      </>
                    ) : (
                      <>
                        <span className="font-display font-black text-5xl">£{price}</span>
                        <span className={`text-xs ${p.dark ? "text-[#a8a8a8]" : "text-[#5C5C5C]"}`}>/ month</span>
                      </>
                    )}
                  </div>
                  <div className={`text-[10px] uppercase tracking-widest mt-1 ${p.dark ? "text-[#a8a8a8]" : "text-[#5C5C5C]"}`}>
                    {price == null ? "Annual contract" : cycle === "annual" ? "Billed annually" : "Billed monthly"}
                  </div>

                  <ul className="mt-6 space-y-2.5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 mt-0.5 text-[var(--brand-primary)] flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={p.cta_url || (p.key === "enterprise" ? "/contact-sales" : "/register")}
                    data-testid={`plan-${p.key}-cta`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 py-3 px-4 font-semibold text-sm transition ${
                      p.highlight || p.dark
                        ? "bg-[var(--brand-primary)] text-white hover:bg-[#B83A28]"
                        : "border border-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-[#FDFCF8]"
                    }`}
                  >
                    {p.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-[#5C5C5C] mt-6 italic">
            Payments are processed securely by Stripe. Refunds available within 14 days, <strong>subject to owner approval</strong>. Charities, co-ops and small businesses: <a href="mailto:admin@oceanash.org" className="underline hover:text-[var(--brand-primary)]">email us</a> for a discount code.
          </p>
        </div>
      </section>

      {/* Per-job boosts (still available on top of subscription) */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="label-eyebrow">Optional · Per-role boosts</div>
        <h2 className="font-display font-black text-3xl mt-3 tracking-tight">
          Need a single role to fly higher? Boost it one-off.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, name: "Standard", price: "£0", desc: "Default listing order" },
            { icon: Sparkles, name: "Featured", price: "£10", desc: "Floats above standard roles · one-time" },
            { icon: Star, name: "Premium Spotlight", price: "£20", desc: "Pinned top + Spotlight badge · 30 days" },
          ].map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.name} className="border border-[#E2E0D8] p-5 flex items-center gap-4">
                <Icon className="w-6 h-6 text-[var(--brand-primary)] flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-display font-bold">{b.name}</div>
                  <div className="text-xs text-[#5C5C5C]">{b.desc}</div>
                </div>
                <div className="font-display font-black text-2xl">{b.price}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#E2E0D8]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="border border-[#0F0F0F] p-10 lg:p-14 flex flex-col lg:flex-row gap-6 items-start lg:items-end justify-between">
            <div>
              <div className="label-eyebrow">Ready when you are</div>
              <h2 className="font-display font-black text-4xl lg:text-5xl mt-3 tracking-tight">
                Start in 60 seconds. <span className="text-[var(--brand-primary)]">No credit card.</span>
              </h2>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Link to="/register" data-testid="pricing-cta-register" className="btn-terra">Create account</Link>
              <Link to="/jobs" data-testid="pricing-cta-browse" className="btn-ghost">See live roles</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
